import { useCallback, useEffect, useRef, useState } from 'react'
import { Square, Video } from 'lucide-react'

const MAX_DURATION_S = 300 // 5 min

function _formatTime(seconds) {
    const m = Math.floor(seconds / 60).toString().padStart(2, '0')
    const s = Math.floor(seconds % 60).toString().padStart(2, '0')
    return `${m}:${s}`
}

/**
 * Screen recording via getDisplayMedia + MediaRecorder.
 * Browser native picker handles consent — we add only the warning text and
 * the persistent recording pill (red dot + mm:ss + Stop).
 *
 * onComplete({ blob, durationSeconds, mime })
 */
export default function VideoRecorder({ onComplete, onCancel }) {
    const [recording, setRecording] = useState(false)
    const [elapsed, setElapsed] = useState(0)
    const [error, setError] = useState('')
    const mediaRef = useRef(null)
    const streamRef = useRef(null)
    const chunksRef = useRef([])
    const startedAtRef = useRef(0)
    const tickerRef = useRef(null)

    const _start = async () => {
        setError('')
        if (typeof navigator === 'undefined' || !navigator.mediaDevices?.getDisplayMedia) {
            setError('Запись экрана не поддерживается этим браузером.')
            return
        }
        let stream
        try {
            stream = await navigator.mediaDevices.getDisplayMedia({
                video: { frameRate: { ideal: 15, max: 24 } },
                audio: false,
            })
        } catch (e) {
            setError(e?.name === 'NotAllowedError' ? 'Разрешение не выдано.' : `Не удалось начать запись: ${e?.message || 'unknown'}`)
            return
        }
        streamRef.current = stream

        const mime = MediaRecorder.isTypeSupported('video/webm;codecs=vp9')
            ? 'video/webm;codecs=vp9'
            : MediaRecorder.isTypeSupported('video/webm')
                ? 'video/webm'
                : 'video/mp4'
        const rec = new MediaRecorder(stream, { mimeType: mime, videoBitsPerSecond: 800_000 })
        mediaRef.current = rec
        chunksRef.current = []
        rec.ondataavailable = (e) => {
            if (e.data && e.data.size > 0) chunksRef.current.push(e.data)
        }
        rec.onstop = () => {
            const blob = new Blob(chunksRef.current, { type: mime.split(';')[0] })
            const duration = (Date.now() - startedAtRef.current) / 1000
            stream.getTracks().forEach((t) => t.stop())
            streamRef.current = null
            mediaRef.current = null
            setRecording(false)
            if (tickerRef.current) clearInterval(tickerRef.current)
            tickerRef.current = null
            onComplete?.({ blob, durationSeconds: duration, mime: mime.split(';')[0] })
        }
        // User can stop the share from the browser's own indicator (Chrome shows
        // "Stop sharing" — wire that to our recorder.
        stream.getVideoTracks()[0].addEventListener('ended', () => {
            if (rec.state !== 'inactive') rec.stop()
        })

        startedAtRef.current = Date.now()
        rec.start(1000)
        setRecording(true)
        setElapsed(0)
        tickerRef.current = setInterval(() => {
            const s = (Date.now() - startedAtRef.current) / 1000
            setElapsed(s)
            if (s >= MAX_DURATION_S && rec.state !== 'inactive') rec.stop()
        }, 250)
    }

    const _stop = useCallback(() => {
        if (mediaRef.current && mediaRef.current.state !== 'inactive') {
            mediaRef.current.stop()
        }
    }, [])

    useEffect(() => () => {
        if (tickerRef.current) clearInterval(tickerRef.current)
        if (streamRef.current) streamRef.current.getTracks().forEach((t) => t.stop())
    }, [])

    const remaining = Math.max(0, MAX_DURATION_S - elapsed)
    const warningWindow = remaining <= 30

    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            <p style={{ margin: 0, fontSize: 12, color: 'var(--color-text-muted)' }}>
                Запись экрана до 5 минут. Браузер спросит, какое окно показать. Не показывайте окна с данными других кандидатов.
            </p>
            {error && (
                <div style={{ padding: 8, background: 'var(--color-danger-bg)', color: 'var(--color-danger)', borderRadius: 6, fontSize: 12 }}>
                    {error}
                </div>
            )}
            {!recording ? (
                <div style={{ display: 'flex', gap: 8 }}>
                    <button type="button" className="btn btn--secondary btn--sm" onClick={onCancel}>Отмена</button>
                    <button type="button" className="btn btn--primary btn--sm" onClick={_start} style={{ marginLeft: 'auto' }}>
                        <Video size={14} style={{ marginRight: 6 }} />
                        Начать запись
                    </button>
                </div>
            ) : (
                <div style={{
                    display: 'flex', alignItems: 'center', gap: 12,
                    padding: 12, borderRadius: 10,
                    background: warningWindow ? 'var(--color-danger-bg)' : 'color-mix(in srgb, var(--color-accent) 10%, transparent)',
                }}>
                    <span style={{
                        width: 10, height: 10, borderRadius: '50%',
                        background: 'var(--color-danger)',
                        animation: 'pulse 1.4s ease-in-out infinite',
                    }} />
                    <span style={{ fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>
                        {_formatTime(elapsed)} / 05:00
                    </span>
                    {warningWindow && (
                        <span style={{ color: 'var(--color-danger)', fontSize: 12 }}>
                            до конца {_formatTime(remaining)}
                        </span>
                    )}
                    <button type="button" className="btn btn--primary btn--sm" onClick={_stop} style={{ marginLeft: 'auto' }}>
                        <Square size={14} style={{ marginRight: 6 }} />
                        Остановить
                    </button>
                </div>
            )}
        </div>
    )
}
